import { useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';

export interface SearchableOption { value: string; label: string }

export function SearchableSelect({ label, value, selectedLabel, options, search, onSearchChange, onChange, disabled, loading, error, placeholder = 'Busque ou selecione…' }: {
  label: string; value: string; selectedLabel?: string; options: SearchableOption[];
  search: string; onSearchChange: (search: string) => void; onChange: (option: SearchableOption) => void;
  disabled?: boolean; loading?: boolean; error?: string; placeholder?: string;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const close = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return <div ref={container} className="relative min-w-0" onBlur={(event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }} onKeyDown={(event) => {
    if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!open) { if (!disabled) setOpen(true); return; }
      const choices = Array.from(container.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? []);
      const current = choices.indexOf(document.activeElement as HTMLButtonElement);
      if (choices.length) {
        event.preventDefault();
        const next = current < 0 ? (event.key === 'ArrowDown' ? 0 : choices.length - 1) : (current + (event.key === 'ArrowDown' ? 1 : -1) + choices.length) % choices.length;
        choices[next].focus();
      }
    }
  }}>
    <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-gray-700">{label}</label>
    <Button ref={trigger} id={id} variant="outline" type="button" role="combobox" aria-expanded={open && !disabled}
      aria-haspopup="listbox" aria-controls={`${id}-options`} disabled={disabled}
      className="w-full min-w-0 justify-between text-left" onClick={() => { setOpen(!open); onSearchChange(''); }}>
      <span className="truncate">{selectedLabel ?? options.find((option) => option.value === value)?.label ?? placeholder}</span>
      <ChevronDown size={16} className="shrink-0 text-gray-500" />
    </Button>
    {open && !disabled && <div className="absolute left-0 top-full z-30 mt-1 w-full min-w-64 rounded-lg border border-gray-200 bg-white p-2 shadow-lg">
      <Input ref={input} aria-label={`Buscar ${label.toLowerCase()}`} placeholder="Buscar pelo nome…" value={search} onChange={(event) => onSearchChange(event.target.value)} />
      {loading ? <p role="status" className="p-3 text-sm text-gray-500">Buscando…</p> : error ? <p role="alert" className="p-3 text-sm text-red-700">{error}</p> :
        <div id={`${id}-options`} role="listbox" aria-label={label} className="mt-2 max-h-64 overflow-y-auto">
          {!options.length && <p className="p-3 text-sm text-gray-500">Nenhum resultado encontrado.</p>}
          {options.map((option) => <Button key={option.value} type="button" role="option" aria-selected={option.value === value} variant="ghost"
            className="h-auto w-full justify-between gap-2 whitespace-normal py-2 text-left" onClick={() => {
              onChange(option); setOpen(false); onSearchChange(''); trigger.current?.focus();
            }}><span>{option.label}</span>{option.value === value && <Check size={16} className="shrink-0" />}</Button>)}
        </div>}
    </div>}
  </div>;
}
